/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { InitiativeFile } from '../types/initiative';
import { PERMISSION_GROUPS, getPermissionGroupConfiguration } from '@utils/permission-groups';
import { AppConfig } from '../types/app';
import { InitiativeTreeItem } from '../reducers/current-user';
import { getInitiativeTree, getInitiativeTreeData } from './initiativeTree';

export const currentInitiative = (state: RootState) => state.initiative.loaded ? state.initiative.data : undefined;
const userInitiatives = (state: RootState) => state.currentUser.loaded ? state.currentUser.data.initiativeTree : undefined;
export const getRootOrg = (state: RootState) => state.globalData.loaded ? state.globalData.organization : undefined;
export const getRootAppConfig = (state: RootState): AppConfig | undefined => {
  return state.globalData.loaded ? state.globalData.data.appConfig : undefined;
};

export const canUseStaticCustomDashboards = (state: RootState) => {
  if (!state.globalData.loaded) {
    return false;
  }

  return [
    PERMISSION_GROUPS.COMPANY_TRACKER_STARTER
  ].includes(state.globalData.organization.permissionGroup as string)
}

export const getInitiativeMateriality = createSelector([currentInitiative], (initiative) => initiative?.materiality);

export const getLevelUserRoles = createSelector([currentInitiative], (initiative) => {
  if (initiative && initiative.userPermissions) {
    return initiative.userPermissions.permissions;
  }
  return [];
});

export const getInitiativeUsage = createSelector([currentInitiative], (initiative) => {
  return initiative ? initiative.usage : [];
});

export const hasSubscriptionSetup = createSelector([currentInitiative], (initiative) => {
  // Has customer and it is not WWG one
  return Boolean(initiative?.customer) && initiative?.customer?.id !== 'wwg';
});

export const getCurrentInitiativeDocuments = createSelector([currentInitiative], (initiative) => {

  const files: {
    [key: string]: InitiativeFile[]
  } = { videos: [], photos: [], files: [], assurance: [], all: [] };
  if (!initiative || !Array.isArray(initiative.files)) {
    return files;
  }
  const documents = initiative.files;
  for (const document of documents) {
    if (document.ownerId !== initiative._id) {
      files.assurance.push(document);
      continue;
    }

    const mediaType = document.metadata.mimetype.split('/');
    switch (mediaType[0]) {
      case 'video':
        files.videos.push(document);
        break;
      case 'image':
        files.photos.push(document);
        break;
      default:
        files.files.push(document);
    }
  }

  files.all = [...documents];

  return files;
});

export const getUserPermissionGroupIds = createSelector([userInitiatives], (initiativeTree) => {
  const defaultPermissionGroup = PERMISSION_GROUPS.FREE;
  const distinctPermissionGroups = new Set([defaultPermissionGroup]);
  if (Array.isArray(initiativeTree)) {
    initiativeTree
      .filter(i => i.permissionGroup && i.permissionGroup !== defaultPermissionGroup)
      .forEach(i => {
        if (i.permissionGroup) {
          distinctPermissionGroups.add(i.permissionGroup)
        }
      });
  }
  return Array.from(distinctPermissionGroups);
});

export const getUserPermissionGroups = createSelector([getUserPermissionGroupIds], (permissionGroupIds) => {
  return permissionGroupIds.map(id => getPermissionGroupConfiguration(id));
});

const defaultPermissionGroup = PERMISSION_GROUPS.FREE;

function getRecursivePermissionGroup(initiativeId: string | undefined, initiativeTree?: InitiativeTreeItem[]): string {
  if (!initiativeId) {
    return defaultPermissionGroup;
  }
  const initiative = initiativeTree?.find(d => d._id === initiativeId);
  if (!initiative) {
    return defaultPermissionGroup;
  }
  if (initiative.permissionGroup && initiative.permissionGroup !== defaultPermissionGroup) {
    return initiative.permissionGroup;
  }
  if (initiative.parentId) {
    return getRecursivePermissionGroup(initiative.parentId, initiativeTree);
  }
  return defaultPermissionGroup;
}

const getInitiativePermissionGroup = createSelector(
  [
    userInitiatives,
    currentInitiative
  ], (initiativeTree, initiative) => {
    return getRecursivePermissionGroup(initiative?._id, initiativeTree);
  });

/** @deprecated Use appConfig or features instead */
export const isInitiativeCTL = createSelector([getInitiativePermissionGroup], (permissionGroup) => {
  return permissionGroup === PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT
});

export const isSingleOrg = createSelector([getRootOrg, getInitiativeTree], (rootInitiative, initiativeTree) => {
  const parentNode = initiativeTree.children.find((company) => company.id === rootInitiative?._id);
  return parentNode && parentNode.children?.length === 0;
});

export const isRootOrg = createSelector([getRootOrg, currentInitiative], (rootInitiative, initiative) => {
  return rootInitiative?._id === initiative?._id;
});

export const checkIsBottomOfInitiativeTree = createSelector(
  [getInitiativeTreeData, currentInitiative],
  (initiativeTree, initiative) =>
    Boolean(initiativeTree && initiative?._id && !initiativeTree.some((node) => node.parentId === initiative._id))
);
